import React from 'react';
import { useState, useEffect } from 'react';
import dayjs from 'lib/dayjs';
import Icon from 'components/AppIcon';
import ThreeDotLoading from 'components/ui/ThreeDotLoading';
import { toast } from 'react-toastify';

const AdminLogUpdateListModal = ({ isOpen, onClose, onApprove, requests = [], isSubmitting }) => {
  const [selectedRequestIds, setSelectedRequestIds] = useState(new Set());
  const [editTimes, setEditTimes] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [actionType, setActionType] = useState(null);

  useEffect(() => {
    setSelectedRequestIds(new Set());
    setEditMode(false);
    setEditTimes({});
  }, [isOpen, requests]);

  const filteredRequests = React.useMemo(() => {
    const statusOrder = { PENDING: 0, APPROVED: 1, REJECTED: 2 };
    return [...requests].sort((a, b) => {
      return (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3);
    });
  }, [requests]);

  const actionableRequests = filteredRequests.filter((r) => {
    const isProcessedCreate = r.status === 'APPROVED' && r.type === 'CREATE';
    return (r.status === 'PENDING' || r.status === 'APPROVED') && !isProcessedCreate;
  });

  const handleModalClick = (e) => e.stopPropagation();

  if (!isOpen) return null;

  const employeeName = requests[0]?.employeeName || 'Unknown Employee';
  const employeeId = requests[0]?.employeeId || 'N/A';

  const allSelected =
    actionableRequests.length > 0 && selectedRequestIds.size === actionableRequests.length;
  const someSelected =
    selectedRequestIds.size > 0 && selectedRequestIds.size < actionableRequests.length;

  const handleAction = (status) => {
    setActionType(status);
    const payload = [];

    for (const id of selectedRequestIds) {
      const request = requests.find((r) => r.id === id);
      const times = editTimes[id] || {
        check_in: dayjs(request.check_in).format('HH:mm'),
        check_out: request.check_out ? dayjs(request.check_out).format('HH:mm') : '',
      };

      // Combine log date with time to create full datetime
      const logDate = dayjs(request.logDate).format('YYYY-MM-DD');
      const checkInDateTime = dayjs(`${logDate}T${times.check_in}`).toISOString();
      const checkOutDateTime = times.check_out
        ? dayjs(`${logDate}T${times.check_out}`).toISOString()
        : null;

      if (checkOutDateTime && !dayjs(checkOutDateTime).isAfter(dayjs(checkInDateTime))) {
        toast.error(
          `Checkout time must be after check-in time for ${dayjs(request.logDate).format('DD MMM')}`,
        );
        return;
      }

      payload.push({
        id,
        status,
        check_in: checkInDateTime,
        check_out:
          status === 'REJECTED'
            ? request.check_out
              ? dayjs(request.check_out).toISOString()
              : null
            : checkOutDateTime,
      });
    }

    onApprove(payload);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRequestIds(new Set(actionableRequests.map((r) => r.id)));
    } else {
      setSelectedRequestIds(new Set());
    }
  };

  const handleSelectOne = (id, checked) => {
    const newSet = new Set(selectedRequestIds);
    if (checked) {
      newSet.add(id);
    } else {
      newSet.delete(id);
    }
    setSelectedRequestIds(newSet);
  };

  const handleTimeChange = (id, field, value) => {
    const request = requests.find((r) => r.id === id);
    if (!request) return;

    setEditTimes((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {
          check_in: dayjs(request.check_in).format('HH:mm'),
          check_out: request.check_out ? dayjs(request.check_out).format('HH:mm') : '',
        }),
        [field]: value,
      },
    }));
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fadeIn p-4"
    >
      <div
        onClick={handleModalClick}
        className="relative w-full max-w-5xl bg-white rounded-md border border-gray-200 flex flex-col max-h-[90vh] overflow-hidden animate-slideUp"
      >
        {/* Header */}
        <div className="px-8 py-2 border-b border-gray-100 bg-gray-50/50">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Log Update Requests</h2>
              <div className="flex items-center space-x-3 mt-1">
                <span className="text-sm font-medium text-blue-600">{employeeName}</span>
                <span className="text-xs text-gray-400 font-mono">#{employeeId}</span>
              </div>
              {editMode && (
                <p className="text-xs text-gray-500 mt-2">
                  Review and adjust check-in/check-out times before submitting
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors"
            >
              <Icon name="X" size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto p-6">
          <table className="w-full text-left border-collapse">
            <thead>
              {filteredRequests.length > 0 ? (
                <tr className="border-b border-gray-100">
                  <th className="pb-4 pt-2 text-xs font-semibold text-gray-500 uppercase px-2 w-10">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => el && (el.indeterminate = someSelected)}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="pb-4 pt-2 text-xs font-semibold text-gray-500 uppercase">Date</th>
                  <th className="pb-4 pt-2 text-xs font-semibold text-gray-500 uppercase">
                    Check-in <span className="text-[10px] lowercase font-medium">(Old → New)</span>
                  </th>
                  <th className="pb-4 pt-2 text-xs font-semibold text-gray-500 uppercase">
                    Check-out
                  </th>
                  <th className="pb-4 pt-2 text-xs font-semibold text-gray-500 uppercase">
                    Reason
                  </th>
                  <th className="pb-4 pt-2 text-xs font-semibold text-gray-500 uppercase text-right">
                    Type | Status
                  </th>
                </tr>
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-12">
                    <h3 className="text-lg font-medium text-gray-500">No Record Found</h3>
                  </td>
                </tr>
              )}
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredRequests.map((request) => {
                const isProcessedCreate =
                  request.status === 'APPROVED' && request.type === 'CREATE';
                const isActionable =
                  (request.status === 'PENDING' || request.status === 'APPROVED') &&
                  !isProcessedCreate;
                const isSelected = selectedRequestIds.has(request.id);
                const currentEdit = editTimes[request.id] || {
                  check_in: dayjs(request.check_in).format('HH:mm'),
                  check_out: request.check_out ? dayjs(request.check_out).format('HH:mm') : '',
                };

                return (
                  <tr
                    key={request.id}
                    className={`hover:bg-gray-50/50 transition-colors group ${isSelected ? 'bg-blue-50/30' : ''}`}
                  >
                    <td className="py-4 align-top px-2">
                      {isActionable && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleSelectOne(request.id, e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
                        />
                      )}
                    </td>
                    <td className="py-4 align-top">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">
                          {dayjs(request.logDate).format('DD MMM YYYY')}
                        </span>
                        <span className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mt-0.5">
                          {dayjs(request.logDate).format('dddd')}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 align-top">
                      <div className="flex flex-col">
                        <span className="text-[12px] text-gray-400 font-bold uppercase">
                          Old:{' '}
                          {request.originalCheckIn
                            ? dayjs(request.originalCheckIn).format('hh:mm A')
                            : '--:--'}
                        </span>
                        {isSelected && editMode ? (
                          <div className="flex items-center space-x-2 mt-1">
                            <input
                              type="time"
                              value={currentEdit.check_in}
                              onChange={(e) =>
                                handleTimeChange(request.id, 'check_in', e.target.value)
                              }
                              className="text-xs font-bold text-blue-600 bg-blue-50 border border-blue-200 rounded px-2 py-0.5 focus:ring-1 focus:ring-blue-400"
                            />
                          </div>
                        ) : (
                          <span className="text-[13px] font-bold text-primary-600">
                            New: {dayjs(request.check_in).format('hh:mm A')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 align-top">
                      <div className="flex flex-col">
                        <span className="text-[12px] text-gray-400 font-bold uppercase">
                          Old:{' '}
                          {request.originalCheckOut
                            ? dayjs(request.originalCheckOut).format('hh:mm A')
                            : '--:--'}
                        </span>
                        {isSelected && editMode ? (
                          <div className="flex items-center space-x-2 mt-1">
                            <input
                              type="time"
                              value={currentEdit.check_out}
                              onChange={(e) =>
                                handleTimeChange(request.id, 'check_out', e.target.value)
                              }
                              className="text-[13px] font-bold text-blue-600 bg-blue-50 border border-blue-200 rounded px-2 py-0.5 focus:ring-1 focus:ring-blue-400"
                            />
                          </div>
                        ) : (
                          <span className="text-[13px] font-bold text-primary-600">
                            New:{' '}
                            {request.check_out
                              ? dayjs(request.check_out).format('hh:mm A')
                              : '--:--'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 align-top">
                      <p className="text-sm text-gray-600 max-w-[250px]">
                        {request.reason || 'No reason provided'}
                      </p>
                    </td>
                    <td className="py-4 align-top text-right">
                      <div className="flex flex-col items-end gap-1">
                        <span
                          className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter ${request.type === 'CREATE' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-700'}`}
                        >
                          {request.type === 'CREATE' ? 'Create' : 'Update'}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                            request.status?.toUpperCase() === 'PENDING'
                              ? 'bg-yellow-100 text-yellow-700'
                              : request.status?.toUpperCase() === 'APPROVED'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {request.status}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {selectedRequestIds.size > 0 && (
              <span className="font-semibold text-blue-600">
                {selectedRequestIds.size} of {actionableRequests.length} selected
              </span>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all shadow-sm"
            >
              Close
            </button>
            {actionableRequests.length > 0 && (
              <>
                {!editMode ? (
                  <>
                    <button
                      disabled={isSubmitting || selectedRequestIds.size === 0}
                      onClick={() => handleAction('REJECTED')}
                      className={`px-6 py-2 text-sm font-semibold text-white bg-red-600 rounded-xl transition-all shadow-sm ${
                        selectedRequestIds.size === 0
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-red-700 hover:shadow-md'
                      }`}
                    >
                      {isSubmitting && actionType == 'REJECTED' ? <ThreeDotLoading /> : 'Reject'}
                    </button>
                    <button
                      disabled={isSubmitting || selectedRequestIds.size === 0}
                      onClick={() => handleAction('APPROVED')}
                      className={`px-6 py-2 text-sm font-semibold text-white bg-green-600 rounded-xl transition-all shadow-sm ${
                        selectedRequestIds.size === 0
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-green-700 hover:shadow-md'
                      }`}
                    >
                      {isSubmitting && actionType == 'APPROVED' ? <ThreeDotLoading /> : 'Approve'}
                    </button>

                    <button
                      disabled={selectedRequestIds.size === 0}
                      onClick={() => setEditMode(true)}
                      className={`px-6 py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl transition-all shadow-sm ${
                        selectedRequestIds.size === 0
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-blue-700 hover:shadow-md'
                      }`}
                    >
                      Edit & Approve
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setEditMode(false);
                        setEditTimes({});
                      }}
                      className="px-6 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all shadow-sm"
                    >
                      Cancel Edit
                    </button>
                    <button
                      disabled={isSubmitting || selectedRequestIds.size === 0}
                      onClick={() => handleAction('APPROVED')}
                      className={`px-6 py-2 text-sm font-semibold text-white bg-green-600 rounded-xl transition-all shadow-sm ${
                        selectedRequestIds.size === 0
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-green-700 hover:shadow-md'
                      }`}
                    >
                      {isSubmitting ? <ThreeDotLoading /> : 'Approve Selected'}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogUpdateListModal;
